import Modal from '../../../components/modal/modal';
import Card from '../../../components/card/Card';
import P from '../../../components/texts/p/P';

/**
 * Acción que el dashboard quiere confirmar antes de disparar el bulk.
 *   - APPROVE         → bulk de aprobación.
 *   - REJECT          → bulk de declinación (no se usa con este modal).
 *   - REQUEST_CHANGES → bulk de pedido de cambios (no se usa con este modal).
 *
 * IMPORTANTE PARA PHASE 8: aunque el tipo soporta los 3 valores por simetría
 * con `BulkAction` del service, el dashboard SOLO debe abrir este modal con
 * `action='APPROVE'`. Para REJECT / REQUEST_CHANGES, el dashboard abre
 * directamente `DeclineOrRequestChangesModal` (footer dual Declinar /
 * Solicitar cambios; en bulk el comentario es obligatorio). Por eso no
 * tiene sentido un paso de confirmación adicional.
 *
 * Si Phase 8 cambia esa decisión y necesita confirmar también REJ/PEN, los
 * 3 valores ya están preparados con la copy correspondiente.
 */
export type BulkConfirmAction = 'APPROVE' | 'REJECT' | 'REQUEST_CHANGES';

export interface CommitteeBulkConfirmModalProps {
  /** Controla la visibilidad. El padre decide cuándo abrir / cerrar. */
  isOpen: boolean;
  /** Acción a confirmar — define la copy del cuerpo del modal. */
  action: BulkConfirmAction;
  /** Cantidad de carpetas afectadas. Singular si `=== 1`, plural en otro caso. */
  count: number;
  /**
   * Indica si el bulk está en curso. Cuando `true`, el modal muestra un
   * spinner, cambia el texto a "Procesando..." y deshabilita los botones
   * para que el usuario sepa que debe esperar.
   */
  isProcessing?: boolean;
  /**
   * Click en Confirmar — el padre dispara el bulk hacia el BFF
   * (`postCommitteeBulkDecision`) y debe cerrar este modal vía `onCancel`
   * o seteando isOpen=false desde su propio handler.
   */
  onConfirm: () => void;
  /** Cancelar / cerrar — el padre setea isOpen=false. Sin side effects. */
  onCancel: () => void;
}

/**
 * Verbos en español para cada acción, conjugados en plural / singular según
 * la cantidad. Mantener en sync con las decisiones de copy del design
 * (Engram #59) y con `DeclineOrRequestChangesModal` para coherencia visual.
 */
const ACTION_VERB: Record<BulkConfirmAction, { plural: string; singular: string }> = {
  APPROVE: {
    plural: 'aprobar',
    singular: 'aprobar',
  },
  REJECT: {
    plural: 'declinar',
    singular: 'declinar',
  },
  REQUEST_CHANGES: {
    plural: 'solicitar cambios en',
    singular: 'solicitar cambios en',
  },
};

/**
 * CommitteeBulkConfirmModal — paso intermedio de confirmación previo a
 * disparar el bulk hacia el BFF. Se monta como hermano del Table en el
 * dashboard (Phase 8) y se abre solo para la acción APPROVE (ver JSDoc
 * de `BulkConfirmAction`).
 *
 * Estructura: título "Confirmar acción", body con la frase principal y
 * footer con "Cancelar" + "Confirmar". No tiene estado interno — todo se
 * resuelve por props para que el padre tenga control total.
 *
 * Linaje SDD: Engram #59 (design) + #60 (tasks v2) + #70 (apply-phase-6).
 */
const CommitteeBulkConfirmModal: React.FC<CommitteeBulkConfirmModalProps> = ({
  isOpen,
  action,
  count,
  isProcessing = false,
  onConfirm,
  onCancel,
}) => {
  const safeCount = Math.max(count, 0);
  const isPlural = safeCount > 1;
  const verb = isPlural ? ACTION_VERB[action].plural : ACTION_VERB[action].singular;
  const folderWord = isPlural ? 'carpetas' : 'carpeta';

  const confirmMessage = `Vas a ${verb} ${safeCount} ${folderWord}. Confirmá para continuar.`;
  const processingMessage = `Procesando ${safeCount} ${folderWord}...`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={isProcessing ? undefined : onCancel}
      title={isProcessing ? 'Procesando' : 'Confirmar acción'}
      primaryText={isProcessing ? 'Procesando...' : 'Confirmar'}
      primaryVariant='primary'
      primaryActionDisabled={isProcessing}
      loadingPrimaryBtn={isProcessing}
      onPrimaryAction={onConfirm}
      secondaryText={isProcessing ? '' : 'Cancelar'}
      secondaryVariant='secondary'
      onSecondaryAction={onCancel}
    >
      <Card>
        {isProcessing ? (
          <Card display='flex' flexDirection='column' gap='8px'>
            <P type='1' variant='neutral' withMargin={false}>
              {processingMessage}
            </P>
            <P type='2' variant='neutral' modifier='weak' withMargin={false}>
              No cierres la pestaña hasta recibir el resultado.
            </P>
          </Card>
        ) : (
          <P type='1' variant='neutral' withMargin={false}>
            {confirmMessage}
          </P>
        )}
      </Card>
    </Modal>
  );
};

export default CommitteeBulkConfirmModal;
